import axios from 'axios';
import i18next from 'i18next';
import { push } from 'connected-react-router';
import {
  api,
  entityNames,
  resourceTypes,
  INSPECT_DUE_BEFORE_NOW,
  INSPECT_DUE_FROM_NOW,
  INSPECT_STATUS_UPCOMING,
} from 'config/constants';
import { INCLUDE_RESOURCE, options } from 'helpers/cookieHelper';
import { handleVersion } from 'helpers/versionsHelper';
import { showToast } from 'helpers/toast';
import generateUUID from 'helpers/generateUUID';
import { buildInspectionPatches } from 'helpers/inspectionPatchHelper';
import { adoptInspectionResource } from 'helpers/inspectionResourceHelper';
import {
  getStartOfDayTimestamp,
  getEndOfDayTimestamp,
} from 'helpers/dateTimeHelper';
import { findPreset } from 'components/Inspect/InspectDateRange/presets';
import * as type from './types';

// Every sessionStorage key the Inspect page persists, cleared on logout/account change.
export var INSPECT_SESSION_KEYS = [
  'inspectState',
  'inspectSettingsConfig',
  'inspectColumnFilters',
  'inspectColumnSort',
  'inspectColumnDescriptors',
  'inspectColumnDescriptorsVersion',
  'inspectCustomColumns',
  'inspectScheduledColumnFilters',
  'inspectScheduledColumnSort',
  'inspectScheduledColumnDescriptors',
  'inspectScheduledColumnDescriptorsVersion',
  'inspectScheduledCustomColumns',
  'inspectActiveTab',
  'inspectPivot',
  'inspectLandingAnimations',
];

var moment = require('moment');

export function fetchInspections(accountUUID) {
  return function (dispatch) {
    if (!accountUUID) return;

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.GET_INSPECTIONS,
    });

    return axios
      .get(api.GET_ACCOUNT + '/' + accountUUID + '/inspections', options)
      .then(function (res) {
        handleVersion(res);
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.GET_INSPECTIONS,
        });
        var data = (res.data && res.data.data) || {};
        var byAssetType = (data.byAssetType && data.byAssetType.items) || [];
        var byInspection = (data.byInspection && data.byInspection.items) || [];
        dispatch({
          type: type.SET_INSPECTIONS,
          payload: { byAssetType: byAssetType, byInspection: byInspection },
        });
      })
      .catch(function () {
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.GET_INSPECTIONS,
        });
        dispatch({
          type: type.SET_INSPECTIONS,
          payload: { byAssetType: [], byInspection: [] },
        });
      });
  };
}

export function setCurrentInspection(uuid) {
  return function (dispatch) {
    dispatch({
      type: type.SET_CURRENT_INSPECTION,
      payload: { uuid: uuid },
    });
  };
}

export function setInspectSelection(mode, value) {
  return function (dispatch) {
    dispatch({ type: type.CLEAR_INSPECTION_RECORDS });
    dispatch({ type: type.CLEAR_INSPECTION_SCHEDULED });

    dispatch({
      type: type.SET_INSPECT_SELECTION,
      payload: { mode: mode, value: value },
    });

    // Both 'inspection' and 'inspectionType' resolve a single inspection name;
    // 'inspectionType' additionally carries the asset-type scope in its value.
    if (mode === 'inspection') {
      dispatch({
        type: type.SET_CURRENT_INSPECTION,
        payload: { uuid: value },
      });
    } else if (mode === 'inspectionType') {
      dispatch({
        type: type.SET_CURRENT_INSPECTION,
        payload: { uuid: value && value.inspectionUUID },
      });
    }
  };
}

export function setInspectPivot(pivot) {
  return function (dispatch) {
    dispatch({
      type: type.SET_INSPECT_PIVOT,
      payload: { pivot: pivot },
    });
  };
}

// Resolve the selected inspection's uuid from the selection state. Prefers the
// resolved currentInspection; falls back to the raw selectionValue (a bare uuid
// string for 'inspection', or the { inspectionUUID } object for 'inspectionType').
function selectedInspectionUUID(inspectState) {
  var current = inspectState.currentInspection;
  if (current && current.uuid) return current.uuid;
  var value = inspectState.selectionValue;
  if (typeof value === 'string') return value;
  if (value && value.inspectionUUID) return value.inspectionUUID;
  return '';
}

// Stable identity for the current selection. If this changes between request
// dispatch and response arrival, the response is for a stale selection and
// must be discarded — otherwise pages from a prior selection can merge into
// the new one (especially via the load-more concat path). Serializes
// mode + inspectionUUID + assetTypeValue + the assetType hash/value pair.
export function selectionKey(inspectState) {
  var state = inspectState || {};
  var mode = state.selectionMode || 'all';
  var value = state.selectionValue;

  var inspectionUUID = selectedInspectionUUID(state);

  var assetTypeValue = '';
  if (
    value &&
    typeof value === 'object' &&
    value.assetTypeValue !== null &&
    value.assetTypeValue !== undefined
  ) {
    assetTypeValue = value.assetTypeValue;
  }

  var atHash = '';
  var atValue = '';
  if (mode === 'assetType' && value && typeof value === 'object') {
    atHash = value.hash || '';
    atValue =
      value.value !== null && value.value !== undefined ? value.value : '';
  }

  return (
    mode +
    ':' +
    inspectionUUID +
    ':' +
    assetTypeValue +
    ':' +
    atHash +
    '|' +
    atValue
  );
}

// The single source of truth for "selection -> query params", used identically
// by History, Scheduled, and Performance. Returns an array of "key=value"
// strings (values encodeURIComponent'd) per the selection mode:
//   all            -> []
//   inspection     -> [ inspectionUUID ]
//   inspectionType -> [ inspectionUUID, assetTypeValue ]
//   assetType      -> [ assetTypeFieldPathHash, assetTypeValue ]
export function buildInspectionSelectionParams(inspectState) {
  var state = inspectState || {};
  var mode = state.selectionMode || 'all';
  var value = state.selectionValue;
  var params = [];

  if (mode === 'inspection') {
    var uuid = selectedInspectionUUID(state);
    if (uuid) params.push('inspectionUUID=' + encodeURIComponent(uuid));
    return params;
  }

  if (mode === 'inspectionType') {
    var typeUUID = selectedInspectionUUID(state);
    if (typeUUID) params.push('inspectionUUID=' + encodeURIComponent(typeUUID));
    var typeValue = value && value.assetTypeValue;
    if (typeValue !== null && typeValue !== undefined && typeValue !== '') {
      params.push('assetTypeValue=' + encodeURIComponent(typeValue));
    }
    return params;
  }

  if (mode === 'assetType') {
    if (value && value.hash) {
      params.push('assetTypeFieldPathHash=' + encodeURIComponent(value.hash));
    }
    if (value && value.value !== null && value.value !== undefined) {
      params.push('assetTypeValue=' + encodeURIComponent(value.value));
    }
    return params;
  }

  // 'all' -> no params.
  return params;
}

function getOperatorForColumn(descriptor) {
  if (descriptor.operator) return descriptor.operator;
  // The result column (History's pass/fail/incomplete selector) and the status
  // column (Scheduled's overdue/neglected/due-soon/scheduled selector) match
  // exactly (Equals) rather than substring (Contains) -- this is also what the
  // Performance KPI cards rely on when they preset result=pass / result=fail
  // and status=overdue / status=neglected.
  if (descriptor.key === 'result') return 'Equals';
  if (descriptor.key === 'status') return 'Equals';
  if (descriptor.key === 'inspection') return 'Contains';
  if (descriptor.type === 'text') return 'Contains';
  if (descriptor.type === 'selector') return 'Contains';
  // dateTime is handled separately in buildHistoryFilterDTO (two date bounds).
  return 'Contains';
}

export function findDescriptorByKey(descriptors, stableKey) {
  if (!descriptors) return null;
  for (var i = 0; i < descriptors.length; i++) {
    if (descriptors[i].key === stableKey) return descriptors[i];
  }
  return null;
}

// Whether a column descriptor may be sorted. The API sets sortable on every
// descriptor (false only for a computed phrase such as Scheduled's
// Frequency). Strict on purpose: a descriptor restored from a session that
// predates the flag counts as unsortable until fresh ones arrive.
export function isSortableDescriptor(descriptor) {
  return !!descriptor && descriptor.sortable === true;
}

// The first date attribute among the column descriptors. The universal
// time-range selector filters this column (the record timestamp).
function findDateTimeDescriptor(descriptors) {
  if (!descriptors) return null;
  for (var i = 0; i < descriptors.length; i++) {
    if (descriptors[i].type === 'dateTime') return descriptors[i];
  }
  return null;
}

// A date-column filter value is "MM/DD/YYYY" (single day) or
// "MM/DD/YYYY - MM/DD/YYYY" (range). Returns { from, to } as MM/DD/YYYY strings
// (to defaults to from for a single day), or null if there's no usable date.
// Dates use slashes, so the lone dash reliably separates the two endpoints.
function parseDateFilterValue(value) {
  if (typeof value !== 'string') return null;
  var parts = value.split('-');
  var from = (parts[0] || '').trim();
  if (!from) return null;
  var to = parts.length >= 2 ? (parts[1] || '').trim() : from;
  return { from: from, to: to || from };
}

// ---------------------------------------------------------------------------
// Date-bounds helpers
// ---------------------------------------------------------------------------

// Returns { from, to } (moment instances or null) for the History (past) side.
// Custom range wins if either customFrom or customTo is set (single-sided range allowed).
// Otherwise resolves the Past preset via findPreset.
// Returns { from: null, to: null } when no applicable selection.
export function historyDateBounds(dateRange) {
  if (!dateRange) return { from: null, to: null };
  if (dateRange.customFrom || dateRange.customTo) {
    return {
      from: dateRange.customFrom || null,
      to: dateRange.customTo || null,
    };
  }
  if (dateRange.past && dateRange.past !== 'all') {
    var pastPreset = findPreset(dateRange.past);
    if (pastPreset) {
      return pastPreset.compute();
    }
  }
  return { from: null, to: null };
}

// Returns { from, to } (moment instances or null) for the Scheduled (future) side.
// Custom range wins if either customFrom or customTo is set (single-sided range allowed).
// Otherwise resolves the Future preset via findPreset.
// Returns { from: null, to: null } when no applicable selection.
export function scheduledDateBounds(dateRange) {
  if (!dateRange) return { from: null, to: null };
  if (dateRange.customFrom || dateRange.customTo) {
    return {
      from: dateRange.customFrom || null,
      to: dateRange.customTo || null,
    };
  }
  if (dateRange.future && dateRange.future !== 'all') {
    var futurePreset = findPreset(dateRange.future);
    if (futurePreset) {
      return futurePreset.compute();
    }
  }
  return { from: null, to: null };
}

// True when two date-range descriptors select the same window. Compares the
// raw descriptor fields (past / future presets and the custom bounds) rather
// than the derived from/to bounds, so re-applying the already-active range is
// detected as a no-op. Mirrors isSameSelection in Inspect.js: used to skip a
// redundant dispatch that would otherwise clear the loaded records without
// changing any fetch key, leaving the tab blank.
export function isSameDateRange(a, b) {
  var x = a || {};
  var y = b || {};
  return (
    (x.past || 'all') === (y.past || 'all') &&
    (x.future || 'all') === (y.future || 'all') &&
    (x.customFrom || null) === (y.customFrom || null) &&
    (x.customTo || null) === (y.customTo || null)
  );
}

// ---------------------------------------------------------------------------
// Filter DTO core (shared by History and Scheduled)
// ---------------------------------------------------------------------------

// Pure core that builds a filter DTO given:
//   columnFilters  - { [stableKey]: value } map
//   descriptors    - array of column descriptor objects
//   dateBounds     - { from: moment|null, to: moment|null }
// Returns { filters, booleanExpression } or null if no active filters.
function buildFilterDTO(columnFilters, descriptors, dateBounds) {
  var filters = [];
  var segments = [];
  var keys = Object.keys(columnFilters);
  for (var k = 0; k < keys.length; k++) {
    var stableKey = keys[k];
    var value = columnFilters[stableKey];
    if (value === undefined || value === null || value === '') continue;
    var col = findDescriptorByKey(descriptors, stableKey);
    if (!col || !col.filterKey) continue;

    // A multi-select option-picker filter stores an array of raw option
    // values. Each value becomes its own Equals filter, OR'd together in the
    // booleanExpression -- the same shape the INSPECT_STATUS_UPCOMING
    // sentinel expands to below.
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      var optionIds = [];
      for (var v = 0; v < value.length; v++) {
        var optionId = filters.length + 1;
        filters.push({
          id: optionId,
          pathHash: col.filterKey,
          operator: 'Equals',
          filter: value[v],
        });
        optionIds.push(optionId);
      }
      if (optionIds.length === 1) {
        segments.push(String(optionIds[0]));
      } else {
        segments.push('(' + optionIds.join(' OR ') + ')');
      }
      continue;
    }

    if (value === INSPECT_DUE_BEFORE_NOW || value === INSPECT_DUE_FROM_NOW) {
      var nowSec = moment().unix();
      var relId = filters.length + 1;
      filters.push({
        id: relId,
        pathHash: col.filterKey,
        operator:
          value === INSPECT_DUE_BEFORE_NOW
            ? 'LessThanOrEqual'
            : 'GreaterThanOrEqual',
        filter: value === INSPECT_DUE_BEFORE_NOW ? nowSec - 1 : nowSec,
      });
      segments.push(String(relId));
      continue;
    }

    // The Upcoming KPI card spans two statuses (due-soon and scheduled), so
    // its sentinel expands to two Equals filters OR'd together in the
    // booleanExpression -- mirroring how a dateTime range groups its two
    // bounds, but with OR instead of AND.
    if (value === INSPECT_STATUS_UPCOMING) {
      var dueSoonId = filters.length + 1;
      filters.push({
        id: dueSoonId,
        pathHash: col.filterKey,
        operator: 'Equals',
        filter: 'due-soon',
      });
      var scheduledId = filters.length + 1;
      filters.push({
        id: scheduledId,
        pathHash: col.filterKey,
        operator: 'Equals',
        filter: 'scheduled',
      });
      segments.push('(' + dueSoonId + ' OR ' + scheduledId + ')');
      continue;
    }

    if (col.type === 'dateTime') {
      // A date filter spans whole days: include everything from the start of
      // the start date through the end of the end date. Date attributes go to
      // the API as unix seconds (UTC), so the MM/DD/YYYY endpoints are resolved
      // to local start-of-day / end-of-day and converted. The API expresses the
      // span as two bounds ANDed together; a single date uses the same day for
      // both.
      var range = parseDateFilterValue(value);
      if (!range) continue;
      var fromMoment = moment(range.from, 'MM/DD/YYYY', true);
      var toMoment = moment(range.to, 'MM/DD/YYYY', true);
      if (!fromMoment.isValid() || !toMoment.isValid()) continue;
      var lowerId = filters.length + 1;
      filters.push({
        id: lowerId,
        pathHash: col.filterKey,
        operator: 'GreaterThanOrEqual',
        filter: getStartOfDayTimestamp(fromMoment),
      });
      var upperId = filters.length + 1;
      filters.push({
        id: upperId,
        pathHash: col.filterKey,
        operator: 'LessThanOrEqual',
        filter: getEndOfDayTimestamp(toMoment),
      });
      segments.push('(' + lowerId + ' AND ' + upperId + ')');
    } else {
      var id = filters.length + 1;
      filters.push({
        id: id,
        pathHash: col.filterKey,
        operator: getOperatorForColumn(col),
        filter: value,
      });
      segments.push(String(id));
    }
  }

  // Universal time-range selector. It scopes the record timestamp to
  // whole-day bounds in unix seconds (UTC), ANDed with any column filters.
  // Each bound is optional: a past-only or future-only selection yields a
  // single bound; a closed range yields both.
  var bounds = dateBounds || { from: null, to: null };
  if (bounds.from || bounds.to) {
    var tsDescriptor = findDateTimeDescriptor(descriptors);
    if (tsDescriptor && tsDescriptor.filterKey) {
      var rangeIds = [];
      if (bounds.from) {
        var fromId = filters.length + 1;
        filters.push({
          id: fromId,
          pathHash: tsDescriptor.filterKey,
          operator: 'GreaterThanOrEqual',
          filter: getStartOfDayTimestamp(bounds.from),
        });
        rangeIds.push(fromId);
      }
      if (bounds.to) {
        var toId = filters.length + 1;
        filters.push({
          id: toId,
          pathHash: tsDescriptor.filterKey,
          operator: 'LessThanOrEqual',
          filter: getEndOfDayTimestamp(bounds.to),
        });
        rangeIds.push(toId);
      }
      if (rangeIds.length === 2) {
        segments.push('(' + rangeIds[0] + ' AND ' + rangeIds[1] + ')');
      } else if (rangeIds.length === 1) {
        segments.push(String(rangeIds[0]));
      }
    }
  }

  if (filters.length === 0) return null;
  return {
    filters: filters,
    booleanExpression: segments.join(' AND '),
  };
}

// Thin wrapper: builds History filter DTO using column filters + Past date bounds.
export function buildHistoryFilterDTO(inspectState) {
  var columnFilters = (inspectState && inspectState.columnFilters) || {};
  var descriptors = (inspectState && inspectState.recordsColumns) || [];
  var bounds = historyDateBounds(
    (inspectState && inspectState.dateRange) || null,
  );
  return buildFilterDTO(columnFilters, descriptors, bounds);
}

// Builds Scheduled filter DTO using scheduled column filters + Future date bounds.
export function buildScheduledFilterDTO(inspectState) {
  var columnFilters =
    (inspectState && inspectState.scheduledColumnFilters) || {};
  var descriptors = (inspectState && inspectState.scheduledColumns) || [];
  var bounds = scheduledDateBounds(
    (inspectState && inspectState.dateRange) || null,
  );
  return buildFilterDTO(columnFilters, descriptors, bounds);
}

// ---------------------------------------------------------------------------
// Sort DTO builders
// ---------------------------------------------------------------------------

// Shared sort DTO core: given a column key, direction, and descriptor array,
// returns "index,direction" or null. An unsortable column yields null even
// if a stale sort key is still sitting in Redux, so it never reaches the API.
function buildSortDTO(columnKey, direction, descriptors) {
  if (!columnKey || !direction) return null;
  var descriptor = findDescriptorByKey(descriptors, columnKey);
  if (!isSortableDescriptor(descriptor)) return null;
  if (descriptor.index === undefined || descriptor.index === null) return null;
  return descriptor.index + ',' + direction;
}

export function buildHistorySortDTO(inspectState) {
  var key = inspectState && inspectState.sortColumnKey;
  var direction = inspectState && inspectState.sortDirection;
  var descriptors = (inspectState && inspectState.recordsColumns) || [];
  return buildSortDTO(key, direction, descriptors);
}

export function buildScheduledSortDTO(inspectState) {
  var key = inspectState && inspectState.scheduledSortColumnKey;
  var direction = inspectState && inspectState.scheduledSortDirection;
  var descriptors = (inspectState && inspectState.scheduledColumns) || [];
  return buildSortDTO(key, direction, descriptors);
}

export function setInspectColumnFilter(columnKey, value) {
  return function (dispatch) {
    dispatch({
      type: type.SET_INSPECT_COLUMN_FILTER,
      payload: { columnKey: columnKey, value: value },
    });
  };
}

export function clearInspectColumnFilters() {
  return function (dispatch) {
    dispatch({ type: type.CLEAR_INSPECT_COLUMN_FILTERS });
  };
}

export function setInspectDateRange(dateRange) {
  return function (dispatch) {
    dispatch({
      type: type.SET_INSPECT_DATE_RANGE,
      payload: { dateRange: dateRange },
    });
  };
}

export function setInspectSort(columnKey, direction) {
  return function (dispatch) {
    dispatch({
      type: type.SET_INSPECT_SORT,
      payload: { columnKey: columnKey, direction: direction },
    });
  };
}

export function setInspectColumns(columns, customColumns) {
  return function (dispatch) {
    dispatch({
      type: type.SET_INSPECT_COLUMNS,
      payload: { columns: columns, customColumns: customColumns },
    });
  };
}

// Build the query string for /inspections/history (and /scheduled) from the
// current selection. Both endpoints — and /performance, which appends from/to —
// are called with these identical params via buildInspectionSelectionParams.
function buildHistoryQuery(state) {
  var params = buildInspectionSelectionParams(state);
  return params.length > 0 ? '?' + params.join('&') : '';
}

// Normalize a row from /inspections/history (and /inspections/scheduled -- this is the
// sole mapper for both) to the shape InspectRecords.js reads. Rows arrive as
// { slateUUID, granularity, timeZone, columns: [{ index, value }, ...] }; the indexed
// values are mapped into a flat object keyed by the descriptor's stable key, and
// granularity/timeZone ride along as-is. Those two are NOT columns -- the API lifts them
// onto the row itself (ListViewDataRowResource) -- so they have to be copied explicitly
// or isDayGranularity() in InspectRecords.js is false for every row and the whole
// day-coarse contract (Due Today badge, zoned date rendering) silently does nothing.
// The API serializes with NullValueHandling.Ignore, so timeZone is ABSENT rather than
// null on a Manual row; undefined is exactly what rowTimeZone() already expects there.
export function normalizeHistoryRow(rawRow, columnsDescriptors) {
  var result = {
    slateUUID: rawRow.slateUUID || '',
    granularity: rawRow.granularity,
    timeZone: rawRow.timeZone,
    // Custom field column values keyed by their cf.* key (stage 3 API). Read by
    // InspectRecords' custom cells; absent when the config has no custom columns.
    custom: rawRow.custom && typeof rawRow.custom === 'object' ? rawRow.custom : {},
  };

  if (!columnsDescriptors || columnsDescriptors.length === 0) {
    return result;
  }

  var indexMap = {};
  var rawColumns = rawRow.columns || [];
  for (var i = 0; i < rawColumns.length; i++) {
    indexMap[rawColumns[i].index] = rawColumns[i].value;
  }

  for (var j = 0; j < columnsDescriptors.length; j++) {
    var descriptor = columnsDescriptors[j];
    var hasEntry = indexMap[descriptor.index] !== undefined;
    if (descriptor.type === 'dateTime') {
      result[descriptor.key] = hasEntry ? indexMap[descriptor.index] : null;
    } else {
      result[descriptor.key] = hasEntry ? indexMap[descriptor.index] : '';
    }
  }
  return result;
}

// Header carrying a paging continuation token -- the same one the history
// and scheduled requests all page with.
export var CONTINUATION_HEADER = 'Continuation-Token';

// Request options carrying `token` as the continuation header, cloned (options
// and headers both) so the shared module-level `options` is never mutated. A
// falsy token means "first page" and adds no header.
function withContinuationToken(requestOptions, token) {
  var next = Object.assign({}, requestOptions);
  next.headers = Object.assign({}, requestOptions.headers || {});
  if (token) next.headers[CONTINUATION_HEADER] = token;
  return next;
}

// Per-fetcher monotonic request sequence: each of fetchInspectionHistory and
// fetchInspectionScheduled bumps its own counter on every call, and a
// response is applied only while it is still the most recent request that
// fetcher dispatched. selectionKey alone does not catch every stale-response
// race: it only changes when the selected inspection/asset-type changes, not
// on a filter, sort, or date-range change, so a slow response for an OLDER
// filter combination could otherwise land after (and be indistinguishable
// from, by selectionKey) a newer, differently-filtered response and
// overwrite it with stale rows.
var historyRequestSeq = 0;
var scheduledRequestSeq = 0;

export function fetchInspectionHistory(params) {
  params = params || {};
  var isNewSet = params.isNewSet !== undefined ? params.isNewSet : true;

  return function (dispatch, getState) {
    var rootState = getState();
    var accountUUID =
      rootState.accounts.currentAccount &&
      rootState.accounts.currentAccount.uuid;
    if (!accountUUID) return;

    historyRequestSeq += 1;
    var requestSeq = historyRequestSeq;

    var inspectState = rootState.inspect;
    var query = buildHistoryQuery(inspectState);
    var token = inspectState.recordsToken;
    var requestKey = selectionKey(inspectState);
    var requestToken = token;

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.GET_INSPECTION_RECORDS,
    });

    var requestOptions = withContinuationToken(options, !isNewSet && token);

    var filterDTO = buildHistoryFilterDTO(inspectState);
    var sortHeader = buildHistorySortDTO(inspectState);
    if (filterDTO) {
      requestOptions.headers['Query-Filters'] = JSON.stringify(
        filterDTO.filters,
      );
      requestOptions.headers['Query-Boolean-Expression'] =
        filterDTO.booleanExpression;
    }
    if (sortHeader) {
      requestOptions.headers['Query-Sort'] = sortHeader;
    }

    return axios
      .get(
        api.GET_ACCOUNT + '/' + accountUUID + '/inspections/history' + query,
        requestOptions,
      )
      .then(function (res) {
        handleVersion(res);
        // Latest-request-wins: a slower, older request resolving after a
        // newer one is dropped even if selectionKey still matches (e.g. only
        // a filter/sort/date-range changed between the two calls).
        if (requestSeq !== historyRequestSeq) return;
        var nowState = getState().inspect;
        if (selectionKey(nowState) !== requestKey) return;
        // For load-more responses, also bail if the token we sent doesn't match
        // what's now in state (selection was reset and a new fetch is in flight).
        if (!isNewSet && nowState.recordsToken !== requestToken) return;
        var body = (res.data && res.data.data) || {};
        var responseColumns = body.columns || null;
        var columnsForNormalize = responseColumns || nowState.recordsColumns;
        var rawRows = body.data || [];
        var continuationToken = body.continuationToken || null;
        var rowCount = body.rowCount;
        var normalized = rawRows.map(function (r) {
          return normalizeHistoryRow(r, columnsForNormalize);
        });
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.GET_INSPECTION_RECORDS,
        });
        var recordsPayload = {
          records: normalized,
          token: continuationToken,
          rowCount: rowCount,
          isNewSet: isNewSet,
        };
        if (responseColumns) {
          recordsPayload.columns = responseColumns;
        }
        // Page 1 always resets the custom set (absent = none), so a scope change never
        // leaves the previous scope's custom columns up. Later pages carry none.
        if (isNewSet) {
          recordsPayload.customColumns = Array.isArray(body.customColumns)
            ? body.customColumns
            : [];
        }
        dispatch({
          type: type.SET_INSPECTION_RECORDS,
          payload: recordsPayload,
        });
      })
      .catch(function () {
        if (requestSeq !== historyRequestSeq) return;
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.GET_INSPECTION_RECORDS,
          hasErrors: true,
        });
      });
  };
}

export function clearInspectionRecords() {
  return function (dispatch) {
    dispatch({ type: type.CLEAR_INSPECTION_RECORDS });
  };
}

export function fetchInspectionScheduled(params) {
  params = params || {};
  var isNewSet = params.isNewSet !== undefined ? params.isNewSet : true;

  return function (dispatch, getState) {
    var rootState = getState();
    var accountUUID =
      rootState.accounts.currentAccount &&
      rootState.accounts.currentAccount.uuid;
    if (!accountUUID) return;

    scheduledRequestSeq += 1;
    var requestSeq = scheduledRequestSeq;

    var inspectState = rootState.inspect;
    var query = buildHistoryQuery(inspectState);
    var token = inspectState.scheduledToken;
    var requestKey = selectionKey(inspectState);
    var requestToken = token;

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.GET_INSPECTION_SCHEDULED,
    });

    var requestOptions = withContinuationToken(options, !isNewSet && token);

    var filterDTO = buildScheduledFilterDTO(inspectState);
    var sortHeader = buildScheduledSortDTO(inspectState);
    if (filterDTO) {
      requestOptions.headers['Query-Filters'] = JSON.stringify(
        filterDTO.filters,
      );
      requestOptions.headers['Query-Boolean-Expression'] =
        filterDTO.booleanExpression;
    }
    if (sortHeader) {
      requestOptions.headers['Query-Sort'] = sortHeader;
    }

    return axios
      .get(
        api.GET_ACCOUNT + '/' + accountUUID + '/inspections/scheduled' + query,
        requestOptions,
      )
      .then(function (res) {
        handleVersion(res);
        // Latest-request-wins: a slower, older request resolving after a
        // newer one is dropped even if selectionKey still matches (e.g. only
        // a filter/sort/date-range changed between the two calls).
        if (requestSeq !== scheduledRequestSeq) return;
        var nowState = getState().inspect;
        if (selectionKey(nowState) !== requestKey) return;
        // For load-more responses, also bail if the token we sent doesn't match
        // what's now in state (selection was reset and a new fetch is in flight).
        if (!isNewSet && nowState.scheduledToken !== requestToken) return;
        var body = (res.data && res.data.data) || {};
        var responseColumns = body.columns || null;
        var columnsForNormalize = responseColumns || nowState.scheduledColumns;
        var rawRows = body.data || [];
        var continuationToken = body.continuationToken || null;
        var rowCount = body.rowCount;
        var normalized = rawRows.map(function (r) {
          return normalizeHistoryRow(r, columnsForNormalize);
        });
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.GET_INSPECTION_SCHEDULED,
        });
        var scheduledPayload = {
          records: normalized,
          token: continuationToken,
          rowCount: rowCount,
          isNewSet: isNewSet,
        };
        if (responseColumns) {
          scheduledPayload.columns = responseColumns;
        }
        // Page 1 always resets the custom set (absent = none); see the History fetch.
        if (isNewSet) {
          scheduledPayload.customColumns = Array.isArray(body.customColumns)
            ? body.customColumns
            : [];
        }
        dispatch({
          type: type.SET_INSPECTION_SCHEDULED,
          payload: scheduledPayload,
        });
      })
      .catch(function () {
        if (requestSeq !== scheduledRequestSeq) return;
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.GET_INSPECTION_SCHEDULED,
          hasErrors: true,
        });
      });
  };
}

// Build the full query string for /inspections/performance: the same selection
// params History uses (via buildInspectionSelectionParams), plus the toolbar's
// PAST/FUTURE date bounds as `from`/`to` unix seconds. Exported for unit testing.
export function buildPerformanceQuery(inspectState) {
  var query = buildHistoryQuery(inspectState);
  var dateRange = (inspectState && inspectState.dateRange) || null;
  // The endpoint expresses the whole span as a single from/to window: the lower
  // bound (`from`) comes from the Past column, the upper bound (`to`) from the
  // Future column. Past = 'all' omits `from`; Future = 'all' omits `to`. This is
  // what scopes the forward-looking metrics (Overdue / Upcoming) to the selected
  // future window. A custom range supplies both bounds (both helpers resolve
  // customFrom/customTo first).
  var lowerBound = historyDateBounds(dateRange).from;
  var upperBound = scheduledDateBounds(dateRange).to;
  var dateParts = [];
  if (lowerBound) dateParts.push('from=' + getStartOfDayTimestamp(lowerBound));
  if (upperBound) dateParts.push('to=' + getEndOfDayTimestamp(upperBound));
  if (dateParts.length > 0) {
    query += (query ? '&' : '?') + dateParts.join('&');
  }
  return query;
}

// Stable identity of a performance request: selection + resolved date bounds.
// If this changes between dispatch and response, the response is for a stale
// request and must be discarded.
function performanceSignature(inspectState) {
  var dateRange = (inspectState && inspectState.dateRange) || null;
  var lowerBound = historyDateBounds(dateRange).from;
  var upperBound = scheduledDateBounds(dateRange).to;
  var fromSec = lowerBound ? getStartOfDayTimestamp(lowerBound) : '';
  var toSec = upperBound ? getEndOfDayTimestamp(upperBound) : '';
  return selectionKey(inspectState) + '|' + fromSec + '|' + toSec;
}

// Coerce an API count to a non-negative integer, defaulting to 0.
function toPerfCount(value) {
  var n = parseInt(value, 10);
  if (isNaN(n) || n < 0) return 0;
  return n;
}

// Normalize one passRateByInspection entry. `passRate` arrives as a 0-1
// fraction, or null for an inspection with no graded submissions in range.
// Unparseable rates coerce to null; out-of-range rates clamp into [0, 1].
// An entry without an inspectionUUID has no identity to label or select,
// so the caller drops it.
function toPassRateEntry(entry) {
  if (!entry || !entry.inspectionUUID) return null;
  var rate = parseFloat(entry.passRate);
  var passRate = isNaN(rate) ? null : Math.min(1, Math.max(0, rate));
  return {
    inspectionUUID: entry.inspectionUUID,
    name: entry.name || '',
    passRate: passRate,
  };
}

// Normalize the /inspections/performance response body. Every count coerces
// to a non-negative int EXCEPT `neglected`: the endpoint does not deliver it
// yet, and "absent" must stay distinguishable from "zero neglected assets"
// (absent -> null keeps the health score's neglect penalty inert).
// `passRateByInspection` follows the same absent-vs-delivered pattern:
// null when the endpoint omits it, a normalized (possibly empty) array
// when delivered. Exported for unit testing.
export function parsePerformanceCounts(body) {
  var b = body || {};
  var hasNeglected = b.neglected !== null && b.neglected !== undefined;
  var rates = null;
  if (Array.isArray(b.passRateByInspection)) {
    rates = [];
    for (var i = 0; i < b.passRateByInspection.length; i++) {
      var entry = toPassRateEntry(b.passRateByInspection[i]);
      if (entry) rates.push(entry);
    }
  }
  return {
    passed: toPerfCount(b.passed),
    failed: toPerfCount(b.failed),
    incomplete: toPerfCount(b.incomplete),
    total: toPerfCount(b.total),
    overdue: toPerfCount(b.overdue),
    upcoming: toPerfCount(b.upcoming),
    neglected: hasNeglected ? toPerfCount(b.neglected) : null,
    passRateByInspection: rates,
    corrected: toPerfCount(b.corrected),
    // Lifecycle-completed count (spec section 7): __inspect_state == completed,
    // or the legacy shape (no state field, a Pass/Fail result). Drives the
    // Completion health factor -- see InspectPerformance.computeHealthView --
    // instead of passed + failed, so an entry amended to Incomplete after
    // completion keeps counting as completed without dragging the factor down.
    completed: toPerfCount(b.completed),
  };
}

export function fetchInspectionPerformance() {
  return function (dispatch, getState) {
    var rootState = getState();
    var accountUUID =
      rootState.accounts.currentAccount &&
      rootState.accounts.currentAccount.uuid;
    if (!accountUUID) return;

    var inspectState = rootState.inspect;
    var query = buildPerformanceQuery(inspectState);
    var requestSignature = performanceSignature(inspectState);

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.GET_INSPECTION_PERFORMANCE,
    });

    return axios
      .get(
        api.GET_ACCOUNT +
          '/' +
          accountUUID +
          '/inspections/performance' +
          query,
        options,
      )
      .then(function (res) {
        handleVersion(res);
        // Discard a response whose request is no longer current.
        if (performanceSignature(getState().inspect) !== requestSignature)
          return;
        var body = (res.data && res.data.data) || res.data || {};
        var counts = parsePerformanceCounts(body);
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.GET_INSPECTION_PERFORMANCE,
        });
        dispatch({
          type: type.SET_INSPECTION_PERFORMANCE,
          payload: counts,
        });
      })
      .catch(function () {
        // Ignore a stale error too, then surface as the empty/zero state so the
        // tab shows "no data" rather than an infinite skeleton.
        if (performanceSignature(getState().inspect) !== requestSignature)
          return;
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.GET_INSPECTION_PERFORMANCE,
          hasErrors: true,
        });
        dispatch({
          type: type.SET_INSPECTION_PERFORMANCE,
          payload: {
            passed: 0,
            failed: 0,
            incomplete: 0,
            total: 0,
            overdue: 0,
            upcoming: 0,
            neglected: null,
            passRateByInspection: null,
          },
        });
      });
  };
}

export function clearInspectionPerformance() {
  return function (dispatch) {
    dispatch({ type: type.CLEAR_INSPECTION_PERFORMANCE });
  };
}

export function setInspectScheduledColumnFilter(columnKey, value) {
  return function (dispatch) {
    dispatch({
      type: type.SET_INSPECT_SCHEDULED_COLUMN_FILTER,
      payload: { columnKey: columnKey, value: value },
    });
  };
}

export function clearInspectScheduledColumnFilters() {
  return function (dispatch) {
    dispatch({ type: type.CLEAR_INSPECT_SCHEDULED_COLUMN_FILTERS });
  };
}

export function setInspectScheduledSort(columnKey, direction) {
  return function (dispatch) {
    dispatch({
      type: type.SET_INSPECT_SCHEDULED_SORT,
      payload: { columnKey: columnKey, direction: direction },
    });
  };
}

export function setInspectScheduledColumns(columns, customColumns) {
  return function (dispatch) {
    dispatch({
      type: type.SET_INSPECT_SCHEDULED_COLUMNS,
      payload: { columns: columns, customColumns: customColumns },
    });
  };
}

export function fetchInspectionSettings(inspectionUUID) {
  return function (dispatch, getState) {
    var state = getState();
    var accountUUID =
      state.accounts.currentAccount && state.accounts.currentAccount.uuid;
    if (!accountUUID) {
      dispatch({
        type: type.FETCH_ERROR,
        entityName: entityNames.GET_INSPECTION,
      });
      return;
    }

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.GET_INSPECTION,
    });

    return axios
      .get(
        api.GET_ACCOUNT + '/' + accountUUID + '/inspections/' + inspectionUUID,
        options,
      )
      .then(function (res) {
        handleVersion(res);
        var raw = (res.data && res.data.data) || res.data || {};
        var settingsFields = getState().slateViews.settingsFields;
        var adopted = adoptInspectionResource(raw, settingsFields);
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.GET_INSPECTION,
        });
        dispatch({
          type: type.SET_INSPECTION_SETTINGS,
          payload: {
            settings: adopted,
            rawSettings: raw,
            inspectionUUID: inspectionUUID,
          },
        });
      })
      .catch(function (err) {
        if (err && err.response && err.response.status === 404) {
          dispatch({
            type: type.FETCH_ERROR,
            entityName: entityNames.GET_INSPECTION,
            hasErrors: { notFound: true },
          });
          var inspectPath = '/accounts/' + (accountUUID || '') + '/inspect';
          dispatch(push(inspectPath));
          return;
        }
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.GET_INSPECTION,
        });
      });
  };
}

export function clearInspectionSettings() {
  return function (dispatch) {
    dispatch({ type: type.CLEAR_INSPECTION_SETTINGS });
  };
}

// The inspection PATCH url. `recomputeExistingDueDates` is transient -- it is
// not part of the resource and is never persisted, so it rides as a query
// parameter alongside the JSON Patch body rather than as an operation in it.
export function buildInspectionPatchUrl(accountUUID, inspectionUUID, recomputeExistingDueDates) {
  return (
    api.UPDATE_RESOURCE +
    '/' +
    resourceTypes.INSPECTION +
    '/' +
    inspectionUUID +
    '?account-uuid=' +
    accountUUID +
    '&' +
    INCLUDE_RESOURCE +
    (recomputeExistingDueDates ? '&recompute-existing-due-dates=true' : '')
  );
}

// How many asset templates this inspection covers, and how many already carry a
// due date. Advisory read for the Recurrence settings section: resolves null on
// any failure so a missing count hides the banner instead of blocking a save.
// Not a thunk -- it dispatches nothing and the caller holds the result locally.
export function getInspectionScheduleStatus(accountUUID, inspectionUUID) {
  if (!accountUUID || !inspectionUUID) return Promise.resolve(null);
  return axios
    .get(
      api.GET_ACCOUNT +
        '/' +
        accountUUID +
        '/inspections/' +
        inspectionUUID +
        '/schedule-status',
      options,
    )
    .then(function (res) {
      var body = (res.data && res.data.data) || res.data || {};
      return {
        templateCount: toPerfCount(body.templateCount),
        withDueDateCount: toPerfCount(body.withDueDateCount),
        recomputeStatus: body.recomputeStatus || '',
      };
    })
    .catch(function () {
      return null;
    });
}

function patchInspection(inspectionUUID, config, recomputeExistingDueDates) {
  return function (dispatch, getState) {
    var accountUUID =
      getState().accounts.currentAccount &&
      getState().accounts.currentAccount.uuid;
    if (!accountUUID) {
      dispatch({
        type: type.FETCH_ERROR,
        entityName: entityNames.UPDATE_INSPECTION,
      });
      return Promise.reject(new Error('missing accountUUID'));
    }

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.UPDATE_INSPECTION,
    });

    var patches = buildInspectionPatches(config);

    return axios
      .patch(
        buildInspectionPatchUrl(accountUUID, inspectionUUID, recomputeExistingDueDates),
        patches,
        options,
      )
      .then(function (res) {
        handleVersion(res);
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.UPDATE_INSPECTION,
        });
        var savedName = (config && config.name) || '';
        showToast.success(
          (savedName ? savedName + ' ' : '') +
            i18next.t('notifications:successfullySavedInspectionSettings'),
        );
        // A config change can alter the inspections tree and the records query
        // (e.g. a changed inspected field). Reload the tree and drop stale rows
        // so returning to /inspect reflects the change.
        dispatch(fetchInspections(accountUUID));
        dispatch({ type: type.CLEAR_INSPECTION_RECORDS });
        dispatch({ type: type.CLEAR_INSPECTION_SCHEDULED });
        return { uuid: inspectionUUID, data: res.data && res.data.data };
      })
      .catch(function (err) {
        if (err.response) {
          switch (err.response.status) {
            case 400:
              dispatch({
                type: type.FETCH_ERROR,
                entityName: entityNames.UPDATE_INSPECTION,
                hasErrors: { error400: i18next.t('messages:userNotFound') },
              });
              break;
            case 422:
              if (
                err.response.data.validationErrors &&
                err.response.data.validationErrors.dataErrors
              ) {
                dispatch({
                  type: type.FETCH_ERROR,
                  entityName: entityNames.UPDATE_INSPECTION,
                  hasErrors: {
                    dataErrors: err.response.data.validationErrors.dataErrors,
                  },
                });
              } else {
                dispatch({
                  type: type.ERROR_DIALOG_OPEN,
                  uuid: err.response.data.correlationUUID || null,
                });
              }
              break;
            case 409:
              // Any 409 is a save conflict: nothing was saved. Keyed on the HTTP
              // status, never on the API errorCode.
              dispatch({
                type: type.FETCH_ERROR,
                entityName: entityNames.UPDATE_INSPECTION,
                hasErrors: { error409: i18next.t('messages:changedWhileSaving') },
              });
              break;
            default:
              dispatch({
                type: type.FETCH_ERROR,
                entityName: entityNames.UPDATE_INSPECTION,
                hasErrors:
                  err.response.data && err.response.data.correlationUUID
                    ? err.response.data.correlationUUID
                    : null,
              });
          }
        } else {
          dispatch({
            type: type.FETCH_ERROR,
            entityName: entityNames.UPDATE_INSPECTION,
          });
        }
        return Promise.reject(err);
      });
  };
}

export function createInspection(config) {
  // A brand-new inspection has no assets with due dates yet, so there is
  // nothing to recompute.
  return patchInspection(generateUUID(), config, false);
}

export function updateInspection(inspectionUUID, config, recomputeExistingDueDates) {
  return patchInspection(inspectionUUID, config, !!recomputeExistingDueDates);
}

export var DEFICIENCY_ACTIONS = {
  MARK_DEFICIENT: 'markDeficient',
  CLOSE_OUT: 'closeOut',
  REVERT: 'revertToComputed',
};

export function buildDeficiencyUrl(accountUUID, inspectionUUID, slateUUID) {
  return (
    api.GET_ACCOUNT +
    '/' +
    accountUUID +
    '/inspections/' +
    inspectionUUID +
    '/records/' +
    slateUUID +
    '/deficiency'
  );
}

// The API serves History/Scheduled/Performance from Elasticsearch, and a slate
// write is indexed by a BACKGROUND queue (IndexSlateInESFunction), so a
// refetch fired right after a void or deficiency write races the index and
// can return the pre-write row. Writes therefore patch the affected row in
// the store immediately and refetch once after this delay, letting server
// truth replace the patch.
export var INSPECT_REINDEX_REFRESH_DELAY_MS = 3000;

var reindexRefreshTimer = null;

// Schedules the post-write refetch of History, Scheduled and Performance.
// A second write inside the window restarts the timer, so a burst of writes
// refetches once. Skipped when the account changed in the meantime; each
// fetch also returns early without an account, and drops a response whose
// selection has since changed.
export function scheduleInspectReindexRefresh() {
  return function (dispatch, getState) {
    var account = getState().accounts.currentAccount;
    var accountUUID = account && account.uuid;
    if (reindexRefreshTimer) clearTimeout(reindexRefreshTimer);
    reindexRefreshTimer = setTimeout(function () {
      reindexRefreshTimer = null;
      var nowAccount = getState().accounts.currentAccount;
      if (!nowAccount || nowAccount.uuid !== accountUUID) return;
      dispatch(fetchInspectionHistory({ isNewSet: true }));
      dispatch(fetchInspectionScheduled({ isNewSet: true }));
      dispatch(fetchInspectionPerformance());
    }, INSPECT_REINDEX_REFRESH_DELAY_MS);
  };
}

// Cancels a pending post-write reindex refresh without firing it. The timer
// is a raw setTimeout with no unmount/cleanup wiring of its own (it is
// scheduled well after the component that started it may be gone), so
// Inspect's componentWillUnmount and its account-switch branch both call
// this -- otherwise a write just before navigating away, or just before
// switching accounts, fires History/Scheduled/Performance refetches for a
// component that no longer exists (or, without the account-switch call, for
// an account the user has already left before the same-account check below
// would have caught it).
export function cancelInspectReindexRefresh() {
  return function () {
    if (reindexRefreshTimer) {
      clearTimeout(reindexRefreshTimer);
      reindexRefreshTimer = null;
    }
  };
}

export function patchInspectionHistoryRecord(slateUUID, changes) {
  return {
    type: type.PATCH_INSPECTION_HISTORY_RECORD,
    payload: { slateUUID: slateUUID, changes: changes },
  };
}

// The History row fields a deficiency response (InspectionDeficiencyResource:
// result, deficiencyStatus, resultSource) maps onto -- the same three
// resultChipInfo/availableDeficiencyActions derive the chip and menu from.
// An absent value is the API's null (NullValueHandling.Ignore), which a
// History row carries as '' (normalizeHistoryRow). Null when the response
// has no result to show, so a malformed body never blanks the row.
export function deficiencyRowChanges(resource) {
  if (!resource || !resource.result) return null;
  return {
    result: resource.result,
    deficiencyStatus: resource.deficiencyStatus || '',
    resultSource: resource.resultSource || '',
  };
}

// Applies one deficiency transition to a record, patches its History row from
// the response, then refetches History, Scheduled and Performance after the
// reindex delay. Rejects with an Error whose message is safe to show in the
// dialog.
export function postDeficiencyAction(inspectionUUID, slateUUID, action, note) {
  return function (dispatch, getState) {
    var accountUUID =
      getState().accounts.currentAccount &&
      getState().accounts.currentAccount.uuid;
    if (!accountUUID) return Promise.reject(new Error('missing accountUUID'));

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.POST_INSPECTION_DEFICIENCY,
    });

    return axios
      .post(
        buildDeficiencyUrl(accountUUID, inspectionUUID, slateUUID),
        { action: action, note: note },
        options,
      )
      .then(function (res) {
        handleVersion(res);
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.POST_INSPECTION_DEFICIENCY,
        });
        showToast.success(i18next.t('notifications:inspectionRecordUpdated'));
        var resource = (res.data && res.data.data) || {};
        var changes = deficiencyRowChanges(resource);
        if (changes) dispatch(patchInspectionHistoryRecord(slateUUID, changes));
        dispatch(scheduleInspectReindexRefresh());
        return resource;
      })
      .catch(function (err) {
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.POST_INSPECTION_DEFICIENCY,
        });
        var message = i18next.t('messages:genericError');
        var body = err && err.response && err.response.data;
        if (
          err &&
          err.response &&
          err.response.status === 422 &&
          body &&
          body.validationErrors &&
          body.validationErrors.dataErrors
        ) {
          message =
            body.validationErrors.dataErrors.action ||
            body.validationErrors.dataErrors.note ||
            message;
        } else if (err && err.response && err.response.status === 503) {
          message = temporarilyUnavailableMessage(err);
        }
        return Promise.reject(new Error(message));
      });
  };
}

export function buildInspectionRecordActivityUrl(
  accountUUID,
  inspectionUUID,
  slateUUID,
) {
  return (
    api.GET_ACCOUNT +
    '/' +
    accountUUID +
    '/inspections/' +
    inspectionUUID +
    '/records/' +
    slateUUID +
    '/activity'
  );
}

// One entry's full timeline (spec section 7, "Timeline"): the engine and
// deficiency log, the completion (with backfill provenance), each amendment
// and the void, sorted by time. This is what the History row menu's "View
// Log" now reads from -- History no longer carries an activityLog column.
// Resolves to the entries array; the caller (InspectRecords) holds loading
// and error state locally, the same seam postDeficiencyAction uses.
export function fetchInspectionRecordActivity(inspectionUUID, slateUUID) {
  return function (dispatch, getState) {
    var accountUUID =
      getState().accounts.currentAccount &&
      getState().accounts.currentAccount.uuid;
    if (!accountUUID) return Promise.reject(new Error('missing accountUUID'));

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.GET_INSPECTION_ACTIVITY,
    });

    return axios
      .get(
        buildInspectionRecordActivityUrl(
          accountUUID,
          inspectionUUID,
          slateUUID,
        ),
        options,
      )
      .then(function (res) {
        handleVersion(res);
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.GET_INSPECTION_ACTIVITY,
        });
        var body = (res.data && res.data.data) || res.data || {};
        return Array.isArray(body.entries) ? body.entries : [];
      })
      .catch(function (err) {
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.GET_INSPECTION_ACTIVITY,
        });
        // GetRecordTimeline returns 422 via WithDataError("", ...) for the
        // cases a real user hits here (a non-member opening View Log, or a
        // row whose entry no longer matches the resolved inspection), so the
        // message lives under the empty-string key -- the same shape
        // postDeficiencyAction extracts dataErrors.action/note from above.
        var message = i18next.t('messages:genericError');
        var body = err && err.response && err.response.data;
        if (
          err &&
          err.response &&
          err.response.status === 422 &&
          body &&
          body.validationErrors &&
          body.validationErrors.dataErrors
        ) {
          message = body.validationErrors.dataErrors[''] || message;
        }
        return Promise.reject(new Error(message));
      });
  };
}

export function buildInspectionVoidUrl(accountUUID, inspectionUUID, slateUUID) {
  return (
    api.GET_ACCOUNT +
    '/' +
    accountUUID +
    '/inspections/' +
    inspectionUUID +
    '/records/' +
    slateUUID +
    '/void'
  );
}

export function buildInspectionVoidPreviewUrl(
  accountUUID,
  inspectionUUID,
  slateUUID,
) {
  return (
    buildInspectionVoidUrl(accountUUID, inspectionUUID, slateUUID) + '-preview'
  );
}

// The message for a 503: a contended write guard answers 503 code 24
// (TemporarilyUnavailable) with the message as `data`. Callers check the
// status themselves.
export function temporarilyUnavailableMessage(err) {
  var body = err && err.response && err.response.data;
  return body && typeof body.data === 'string' && body.data
    ? body.data
    : i18next.t(
        'inspect:voidBeingUpdated',
        'This inspection is being updated. Try again.',
      );
}

// The message a void/void-preview failure should show in the dialog. The API
// answers every refusal (not a member, not completed, already void, not part
// of this inspection, blank reason) with a 422 whose message sits under the
// empty-string dataErrors key -- WithDataError("", ...), the same shape
// fetchInspectionRecordActivity reads -- and a contended write guard with a
// retryable 503 whose body carries the message as `data`.
export function inspectionVoidErrorMessage(err) {
  var message = i18next.t('messages:genericError');
  var response = err && err.response;
  var body = response && response.data;
  if (!response) return message;
  if (
    response.status === 422 &&
    body &&
    body.validationErrors &&
    body.validationErrors.dataErrors
  ) {
    var dataErrors = body.validationErrors.dataErrors;
    return dataErrors[''] || dataErrors.reason || message;
  }
  if (response.status === 503) return temporarilyUnavailableMessage(err);
  return message;
}

// Previews what voiding one entry would do to its asset's schedule (spec
// sections 6 and 7): { nextDueBefore, nextDueAfter, lastInspectedBefore,
// lastInspectedAfter, changes }, dates in epoch seconds or null. No side
// effects. Resolves to that resource; the Void dialog holds loading and error
// state locally, the same seam fetchInspectionRecordActivity uses.
export function fetchInspectionVoidPreview(inspectionUUID, slateUUID) {
  return function (dispatch, getState) {
    var accountUUID =
      getState().accounts.currentAccount &&
      getState().accounts.currentAccount.uuid;
    if (!accountUUID) return Promise.reject(new Error('missing accountUUID'));

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.GET_INSPECTION_VOID_PREVIEW,
    });

    return axios
      .get(
        buildInspectionVoidPreviewUrl(accountUUID, inspectionUUID, slateUUID),
        options,
      )
      .then(function (res) {
        handleVersion(res);
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.GET_INSPECTION_VOID_PREVIEW,
        });
        return (res.data && res.data.data) || res.data || {};
      })
      .catch(function (err) {
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.GET_INSPECTION_VOID_PREVIEW,
        });
        return Promise.reject(new Error(inspectionVoidErrorMessage(err)));
      });
  };
}

// Voids a completed entry (spec section 6), marks its History row voided at
// once (Void chip, no deficiency/void menu items), then refetches History,
// Scheduled (the void can move the asset's next due date) and Performance
// after the reindex delay. The Scheduled row is not patched: it is keyed by
// the parent asset slate, which a History row does not carry, so there is no
// reliable join -- it catches up on the delayed refetch. Voiding is
// dashboard-only. An already-voided entry
// answers 200 with no write, so it lands here as an ordinary success. Rejects
// with an Error whose message is safe to show in the dialog.
export function postInspectionVoid(inspectionUUID, slateUUID, reason) {
  return function (dispatch, getState) {
    var accountUUID =
      getState().accounts.currentAccount &&
      getState().accounts.currentAccount.uuid;
    if (!accountUUID) return Promise.reject(new Error('missing accountUUID'));

    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.POST_INSPECTION_VOID,
    });

    return axios
      .post(
        buildInspectionVoidUrl(accountUUID, inspectionUUID, slateUUID),
        { reason: reason },
        options,
      )
      .then(function (res) {
        handleVersion(res);
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.POST_INSPECTION_VOID,
        });
        showToast.success(i18next.t('notifications:inspectionRecordVoided'));
        dispatch(patchInspectionHistoryRecord(slateUUID, { state: 'voided' }));
        dispatch(scheduleInspectReindexRefresh());
        return (res.data && res.data.data) || {};
      })
      .catch(function (err) {
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.POST_INSPECTION_VOID,
        });
        return Promise.reject(new Error(inspectionVoidErrorMessage(err)));
      });
  };
}
